import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for enhanced smooth scrolling functionality
 * Provides cross-browser compatible smooth scrolling with performance optimizations
 */
export const useSmoothScroll = () => {
  const animationFrameRef = useRef();

  // Check for reduced motion preference and mobile device
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const isMobile = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia && 
    window.matchMedia('(max-width: 768px)').matches
  );

  // Enhanced smooth scroll function with mobile optimizations
  const smoothScrollTo = useCallback((targetElement, options = {}) => {
    if (!targetElement) return;

    const {
      duration = isMobile.current ? 400 : 800, // Faster on mobile
      offset = 0,
      easing = 'easeInOutQuart',
      callback = null
    } = options;

    // Apply smooth-scroll class to document element during programmatic scroll
    document.documentElement.classList.add('smooth-scroll');

    // Use native smooth scroll if available and not reduced motion
    // Skip custom implementation on mobile to prevent conflicts
    if (('scrollBehavior' in document.documentElement.style && !prefersReducedMotion.current) || isMobile.current) {
      targetElement.scrollIntoView({
        behavior: prefersReducedMotion.current ? 'auto' : 'smooth',
        block: 'start',
        inline: 'nearest'
      });

      // Apply custom offset after native scroll
      if (offset !== 0) {
        setTimeout(() => {
          window.scrollBy(0, -offset);
          if (callback) callback();
          // Remove smooth-scroll class after scroll completes
          document.documentElement.classList.remove('smooth-scroll');
        }, duration / 2);
      } else if (callback) {
        setTimeout(() => {
          callback();
          // Remove smooth-scroll class after scroll completes
          document.documentElement.classList.remove('smooth-scroll');
        }, duration / 2);
      } else {
        // Remove smooth-scroll class after scroll completes
        setTimeout(() => {
          document.documentElement.classList.remove('smooth-scroll');
        }, duration / 2);
      }
      return;
    }

    // Fallback smooth scroll implementation for older browsers
    const startPosition = window.pageYOffset;
    const targetPosition = targetElement.getBoundingClientRect().top + startPosition;
    const finalPosition = targetPosition - offset; // Remove header height calculation
    const distance = finalPosition - startPosition;
    let startTime = null;

    // Easing functions
    const easingFunctions = {
      linear: t => t,
      easeInQuad: t => t * t,
      easeOutQuad: t => t * (2 - t),
      easeInOutQuad: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
      easeInCubic: t => t * t * t,
      easeOutCubic: t => (--t) * t * t + 1,
      easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
      easeInQuart: t => t * t * t * t,
      easeOutQuart: t => 1 - (--t) * t * t * t,
      easeInOutQuart: t => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t
    };

    const easingFunction = easingFunctions[easing] || easingFunctions.easeInOutQuart;

    const animate = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easedProgress = prefersReducedMotion.current ? 1 : easingFunction(progress);
      
      window.scrollTo(0, startPosition + distance * easedProgress);

      if (timeElapsed < duration && !prefersReducedMotion.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Ensure we end at the exact position
        window.scrollTo(0, finalPosition);
        if (callback) callback();
        // Remove smooth-scroll class after scroll completes
        document.documentElement.classList.remove('smooth-scroll');
      }
    };

    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  // Scroll to element by ID
  const scrollToId = useCallback((id, options = {}) => {
    const element = document.getElementById(id);
    if (element) {
      smoothScrollTo(element, options);
    }
  }, [smoothScrollTo]);

  // Scroll to element by selector
  const scrollToSelector = useCallback((selector, options = {}) => {
    const element = document.querySelector(selector);
    if (element) {
      smoothScrollTo(element, options);
    }
  }, [smoothScrollTo]);

  // Scroll to top of page
  const scrollToTop = useCallback((options = {}) => {
    // Apply smooth-scroll class during programmatic scroll
    document.documentElement.classList.add('smooth-scroll');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    if (options.callback) {
      setTimeout(() => {
        options.callback();
        // Remove smooth-scroll class after scroll completes
        document.documentElement.classList.remove('smooth-scroll');
      }, options.duration || 800);
    } else {
      // Remove smooth-scroll class after scroll completes
      setTimeout(() => {
        document.documentElement.classList.remove('smooth-scroll');
      }, options.duration || 800);
    }
  }, []);

  // Scroll to next section
  const scrollToNext = useCallback((currentId, options = {}) => {
    const currentElement = document.getElementById(currentId);
    if (!currentElement) return;

    const allSections = document.querySelectorAll('section[id], div[id]');
    const sectionArray = Array.from(allSections);
    const currentIndex = sectionArray.findIndex(section => section.id === currentId);
    
    if (currentIndex >= 0 && currentIndex < sectionArray.length - 1) {
      const nextSection = sectionArray[currentIndex + 1];
      smoothScrollTo(nextSection, options);
    }
  }, [smoothScrollTo]);

  // Scroll to previous section
  const scrollToPrevious = useCallback((currentId, options = {}) => {
    const currentElement = document.getElementById(currentId);
    if (!currentElement) return;

    const allSections = document.querySelectorAll('section[id], div[id]');
    const sectionArray = Array.from(allSections);
    const currentIndex = sectionArray.findIndex(section => section.id === currentId);
    
    if (currentIndex > 0) {
      const previousSection = sectionArray[currentIndex - 1];
      smoothScrollTo(previousSection, options);
    }
  }, [smoothScrollTo]);

  // Enhanced hash link handler
  const handleHashLinks = useCallback(() => {
    const handleHashLinkClick = (event) => {
      const target = event.target.closest('a[href^="#"]');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href === '#') return;

      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      
      if (targetElement) {
        event.preventDefault();
        scrollToId(targetId, {
          duration: 600,
          easing: 'easeInOutQuart'
        });

        // Update URL hash without jumping
        if (window.history && window.history.pushState) {
          window.history.pushState(null, null, href);
        }
      }
    };

    document.addEventListener('click', handleHashLinkClick);
    return () => document.removeEventListener('click', handleHashLinkClick);
  }, [scrollToId]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    smoothScrollTo,
    scrollToId,
    scrollToSelector,
    scrollToTop,
    scrollToNext,
    scrollToPrevious,
    handleHashLinks
  };
};

/**
 * Scroll performance optimization utilities
 */
export const useScrollPerformance = () => {
  const throttleRef = useRef();

  // Throttled scroll handler for performance
  const createThrottledScrollHandler = useCallback((handler, delay = 16) => {
    return (...args) => {
      if (throttleRef.current) return;
      
      throttleRef.current = setTimeout(() => {
        handler(...args);
        throttleRef.current = null;
      }, delay);
    };
  }, []);

  // Passive scroll event listener setup
  const addPassiveScrollListener = useCallback((handler, element = window) => {
    const options = { passive: true };
    element.addEventListener('scroll', handler, options);
    return () => element.removeEventListener('scroll', handler, options);
  }, []);

  // Intersection observer for scroll-triggered animations
  const createScrollObserver = useCallback((callback, options = {}) => {
    const defaultOptions = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1,
      ...options
    };

    return new IntersectionObserver(callback, defaultOptions);
  }, []);

  return {
    createThrottledScrollHandler,
    addPassiveScrollListener,
    createScrollObserver
  };
};

/**
 * Hook for managing scroll position and direction
 */
export const useScrollPosition = () => {
  const scrollPosition = useRef({ x: 0, y: 0 });
  const scrollDirection = useRef({ x: 'none', y: 'none' });
  const prevScrollPosition = useRef({ x: 0, y: 0 });

  const updateScrollPosition = useCallback(() => {
    const currentX = window.pageXOffset;
    const currentY = window.pageYOffset;

    // Update direction
    scrollDirection.current.x = currentX > prevScrollPosition.current.x ? 'right' : 
                               currentX < prevScrollPosition.current.x ? 'left' : 'none';
    scrollDirection.current.y = currentY > prevScrollPosition.current.y ? 'down' : 
                               currentY < prevScrollPosition.current.y ? 'up' : 'none';

    // Update positions
    prevScrollPosition.current = { ...scrollPosition.current };
    scrollPosition.current = { x: currentX, y: currentY };
  }, []);

  const getScrollPosition = useCallback(() => scrollPosition.current, []);
  const getScrollDirection = useCallback(() => scrollDirection.current, []);

  return {
    updateScrollPosition,
    getScrollPosition,
    getScrollDirection
  };
};
